import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { API_CONFIG } from '../../../utils/apiConfig';
import { FirebaseService } from '../../../services/firebaseService';
import './CustomersPage.css';

// Customer Card Component
const CustomerCard = ({ customer, index }) => {
  return (
    <div key={customer.id || index} className="customer-card">
      <div className="customer-name">{customer.name}</div>
      <div className="customer-date">📅 {customer.createdDate}</div>
    </div>
  );
};

// Main CustomersPage Component
const CustomersPage = () => {
  const { currentUser } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCustomers = async () => {
      let customersData = [];
      
      try {
        setLoading(true);
        
        if (!API_CONFIG.USE_FIREBASE_ONLY && API_CONFIG.BASE_URL && currentUser) {
          try {
            const idToken = await currentUser.getIdToken(true);
            
            const res = await axios.get(`${API_CONFIG.BASE_URL}/api/customers`, {
              headers: { Authorization: `Bearer ${idToken}` },
            });
            
            customersData = Array.isArray(res.data) ? res.data : [];
          } catch (apiError) {
            // Continue to Firebase fallback
          }
        }
        
        // Try Firebase if no results from API or API not available
        if (customersData.length === 0) {
          try {
            const firebaseCustomers = await FirebaseService.getCustomers();
            
            if (firebaseCustomers && firebaseCustomers.length > 0) {
              customersData = firebaseCustomers;
            }
          } catch (firebaseError) {
          }
        }
        
        // Final fallback to localStorage
        if (customersData.length === 0) {
          try {
            const storedCustomers = JSON.parse(localStorage.getItem('customers') || '[]');
            
            if (storedCustomers && storedCustomers.length > 0) {
              customersData = storedCustomers;
            }
          } catch (storageError) {
          }
        }
        
        // Process customers data to match the expected format
        const processedCustomers = Array.isArray(customersData) ? customersData.map(customer => ({
          ...customer,
          id: customer.id || customer._id,
          name: customer.customerName || customer.name || customer.companyName || 'Unknown Customer',
          email: customer.customerContact?.email || customer.email || customer.contactEmail || customer.customerEmail || 'N/A',
          phone: customer.customerContact?.phone || customer.phone || 'N/A',
          address: customer.customerContact?.address || customer.address || 'N/A',
          gstin: customer.customerContact?.gstin || customer.gstin || 'N/A',
          company: customer.company || 'N/A',
          createdDate: customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : 'N/A',
          createdTime: customer.createdAt ? new Date(customer.createdAt).toLocaleTimeString() : 'N/A',
          createdBy: customer.createdBy || 'Unknown',
          customerId: customer.customerId || customer.id || customer._id
        })) : [];

        setCustomers(processedCustomers);
        
      } catch (err) {
        console.error('Error in fetchCustomers:', err);
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
    
    // Set up periodic refresh every 30 seconds to catch new customers
    const refreshInterval = setInterval(fetchCustomers, 30000);
    
    // Listen for storage changes (when new customers are added via CustomerList)
    const handleStorageChange = (e) => {
      if (e.key === 'customers') {
        fetchCustomers();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      clearInterval(refreshInterval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [currentUser]);

  // Filter newly created customers (today)
  const today = new Date().toLocaleDateString();
  const newCustomers = customers.filter(customer => customer.createdDate === today);

  return (
    <div className="customers-page">
      {newCustomers.length > 0 && (
        <div className="customers-section new-customers-section">
          <h3>🆕 Newly Created Today ({newCustomers.length})</h3>
          <div className="customers-grid">
            {newCustomers.map((customer, index) => (
              <CustomerCard key={customer.id || index} customer={customer} index={index} />
            ))}
          </div>
        </div>
      )}

      {/* All Customers Section */}
      <div className="customers-section">
        <h3>All Customers ({customers.length})</h3>
        
        {loading ? (
          <div className="loading">Loading customers...</div>
        ) : (
          <div className="customers-grid">
            {customers.map((customer, index) => (
              <CustomerCard key={customer.id || index} customer={customer} index={index} />
            ))}
          </div>
        )}
        
        {!loading && customers.length === 0 && (
          <div className="no-results">
            <p>No customers found.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomersPage;
